import {
  BufferAttribute,
  BufferGeometry,
  Color,
  DynamicDrawUsage,
  Float32BufferAttribute,
  Mesh,
  Object3D,
  Points,
  PointsMaterial,
  Shader,
  TextureLoader,
  Vector3,
} from "three";

import { PoserClock } from "../../util/clock";
import { Context } from "../../context";
import {
  almostEqual,
  fetchExtractJson,
  randomNormal,
} from "../../../lib/misc/util";
import { defaultScale } from "../../constants";

interface AttributeSet {
  circle: number[];
  square: number[];
}

interface ShaderDetail {
  shader?: Shader;
  attributes?: {
    // make these interleaved
    position: Float32BufferAttribute;
    timer: Float32BufferAttribute;
    state: Float32BufferAttribute;
    color: Float32BufferAttribute;
  };
}

export type Occupancy = "low" | "mid" | "high" | "fixed" | "none";

type FanColors = { [key: string]: string[] };

const up = new Vector3(0, 1, 0);

export class Crowd extends Object3D {
  private contextRef: Context;

  private occupancy: Occupancy;

  private crowdGeom: Mesh;
  private separation: number;

  private shaderDetails: { circle: ShaderDetail; square: ShaderDetail };

  private defaultHeight = 0.1;

  private initFov: number;

  private prevT?: number;

  private shadow: boolean;

  private points: {
    circle?: Points;
    square?: Points;
  } = {};

  private materials: {
    circle: PointsMaterial;
    square: PointsMaterial;
  };

  private tifoColors: {
    circle?: Float32BufferAttribute;
    square?: Float32BufferAttribute;
  } = {};

  private constructor(context: Context, crowdGeom: Mesh, separation: number) {
    super();

    const { camera, options, userOptions } = context;

    this.contextRef = context;

    this.crowdGeom = crowdGeom;
    this.separation = separation;
    this.occupancy = options.stadiumOccupancy ?? "high";

    this.initFov = camera.fov;

    this.shadow = userOptions.hasShadows();

    this.materials = {
      circle: this.makePointsMaterial("circle", 2.5),
      square: this.makePointsMaterial("square", 3.5),
    };
    this.shaderDetails = { circle: {}, square: {} };
  }

  update(clock: PoserClock) {
    if (!this.shaderDetails.circle.attributes) {
      return;
    }

    // TODO: determine if this is required
    for (const detail of Object.values(this.shaderDetails)) {
      if (!detail.shader) return;
      detail.shader.uniforms.fovScale.value =
        this.initFov / this.contextRef.camera.getEffectiveFOV();
    }

    if (clock.relative == this.prevT) {
      return;
    }

    const maxTime = 1 / this.contextRef.state.crowdSpeed;

    const t = clock.relative;
    for (const detail of Object.values(this.shaderDetails)) {
      if (!detail.shader) return;
      detail.shader.uniforms.time.value = t;
      detail.shader.uniforms.maxTime.value = maxTime;
      detail.shader.uniforms.waveEnabled.value =
        this.contextRef.state.waveEnabled;
    }

    const reset =
      this.prevT !== undefined && Math.abs(clock.relative - this.prevT) > 2;

    const energyTest = this.contextRef.state.crowdEnergy * 0.01;
    // TODO: do all this in the shader
    for (const detail of Object.values(this.shaderDetails)) {
      if (!detail.attributes) return;
      for (let i = 0; i < detail.attributes.position.count; i++) {
        if (reset) {
          detail.attributes.timer.setX(i, 0);
          detail.attributes.state.setX(i, Math.random() > 0.5 ? 0 : 1);

          this.prevT = undefined;
        } else {
          const timer = detail.attributes.timer.getX(i);

          // const finished = timer == 0;
          const finished = Math.abs(t - timer) > maxTime;
          if (finished && Math.random() < energyTest) {
            // standup/down
            const state = detail.attributes.state.getX(i);
            detail.attributes.state.setX(i, state ? 0 : 1);

            detail.attributes.timer.setX(i, t);
          }
        }
      }

      detail.attributes.state.needsUpdate = true;
      detail.attributes.timer.needsUpdate = true;
    }

    this.prevT = clock.relative;
  }

  private async makeDots(
    fanColors: FanColors,
    logoLookup?: TextureLookup,
    home?: string,
    away?: string
  ) {
    const occupancy =
      this.occupancy == "low"
        ? 0.04
        : this.occupancy == "mid"
        ? 0.1
        : this.occupancy == "high"
        ? 0.8
        : 0;

    if (this.occupancy == "fixed") {
      return;
    }

    const colorGen = new ColorGenerator(fanColors, logoLookup, home, away);

    this.crowdGeom.visible = false;
    this.crowdGeom.castShadow = false;
    this.crowdGeom.receiveShadow = false;

    const nonIndexed = this.crowdGeom.geometry.toNonIndexed() as BufferGeometry;

    const buffer: BufferAttribute = nonIndexed.attributes[
      "position"
    ] as BufferAttribute;

    const vertices: AttributeSet = {
      circle: [],
      square: [],
    };
    const colors: AttributeSet = {
      circle: [],
      square: [],
    };
    const states: AttributeSet = {
      circle: [],
      square: [],
    };
    const timers: AttributeSet = {
      circle: [],
      square: [],
    };
    const subs: number[] = [];

    const triPoints = [new Vector3(), new Vector3(), new Vector3()];
    const temp = new Vector3();

    const back = new Vector3();

    let hasPrevious = false;

    let topEdgeCnt = 0;

    for (let i = 0; i + 3 < buffer.count; i += 3) {
      triPoints[0].set(buffer.getX(i), buffer.getY(i), buffer.getZ(i));
      triPoints[1].set(
        buffer.getX(i + 1),
        buffer.getY(i + 1),
        buffer.getZ(i + 1)
      );
      triPoints[2].set(
        buffer.getX(i + 2),
        buffer.getY(i + 2),
        buffer.getZ(i + 2)
      );

      const topEdge = getTopEdgeIndices(triPoints.map((p) => p.y));

      if (topEdge) {
        topEdgeCnt++;

        const first = triPoints[topEdge[0]];
        const second = triPoints[topEdge[1]];

        back.subVectors(second, first).cross(up).normalize();

        const distance = first.distanceTo(second);
        const increments = this.separation / distance;
        if (increments < 0.5) {
          for (let alpha = 0; alpha <= 1; alpha += increments) {
            if (
              Math.random() > occupancy &&
              (!hasPrevious || Math.random() < 0.25)
            ) {
              hasPrevious = false;
              continue;
            }

            const randOffset = (Math.random() - 0.5) * 0.01;
            alpha += randOffset;
            temp.lerpVectors(first, second, alpha);

            const randBackOffset = (Math.random() - 0.5) * 0.08;
            temp.addScaledVector(back, 0.2 + randBackOffset);

            temp.y -= 1;

            const squareColor = colorGen.genSpecial(temp);
            if (squareColor) {
              vertices.square.push(temp.x, temp.y, temp.z);

              colors.square.push(...squareColor, 1);
              subs.push(...colorGen.generate(), 1);

              timers.square.push(0);
              states.square.push(0);
            } else {
              vertices.circle.push(temp.x, temp.y, temp.z);

              colors.circle.push(...colorGen.generate(), 1);

              timers.circle.push(0);
              states.circle.push(0);
            }
            hasPrevious = true;
          }
        }
      }
    }
    this.tifoColors.square = new Float32BufferAttribute(subs, 4);
    this.tifoColors.circle = new Float32BufferAttribute(colors.square, 4);

    const missingRows = buffer.count / 6 - topEdgeCnt;
    if (missingRows > 0) {
      console.warn(`missing ${missingRows} crowd rows`);
    }

    // TODO: make these buffers interleaved
    for (const shape of ["circle", "square"]) {
      this.shaderDetails[shape].attributes = {
        position: new Float32BufferAttribute(vertices[shape], 3),
        timer: new Float32BufferAttribute(timers[shape], 1),
        state: new Float32BufferAttribute(states[shape], 1),
        color: new Float32BufferAttribute(colors[shape], 4),
      };

      const geometry = new BufferGeometry();
      geometry.setAttribute(
        "position",
        this.shaderDetails[shape].attributes.position
      );
      geometry.setAttribute(
        "timer",
        this.shaderDetails[shape].attributes.timer
      );
      geometry.setAttribute(
        "state",
        this.shaderDetails[shape].attributes.state
      );
      geometry.setAttribute(
        "color",
        this.shaderDetails[shape].attributes.color
      );

      geometry.computeBoundingSphere();

      (<BufferAttribute>geometry.attributes.position).setUsage(
        DynamicDrawUsage
      );

      const points = new Points(geometry, this.materials[shape]);
      points.receiveShadow = this.shadow;

      points.scale.setScalar(defaultScale);
      this.points[shape] = points;

      this.add(points);
    }
  }

  static async make(
    context: Context,
    crowdGeom: Mesh,
    homeAbbr?: string,
    awayAbbr?: string,
    separation = 0.4
  ): Promise<Crowd> {
    const crowd = new Crowd(context, crowdGeom, separation);

    const response = await fetch(
      `${context.options.assetBase}/stadium/fan.json`
    );
    const fanColors = await fetchExtractJson(response);

    const logoLookup = await TextureLookup.make(
      `${context.options.assetBase}/images/mastercard.png`
    );

    await crowd.makeDots(fanColors, logoLookup, homeAbbr, awayAbbr);

    crowd.tifoEnabled = context.state.tifoEnabled;

    return crowd;
  }

  makePointsMaterial(spriteName: "square" | "circle", size: number) {
    const { options, state } = this.contextRef;
    const sprite = new TextureLoader().load(
      `${options.assetBase}/images/${spriteName}.png`
    );

    const pointMat = new PointsMaterial({
      size,
      vertexColors: true,
      map: sprite,
      alphaTest: 0.5,
      transparent: true,
    });

    pointMat.onBeforeCompile = (shader: Shader) => {
      shader.uniforms.time = { value: 0 };
      shader.uniforms.maxHeight = { value: this.defaultHeight };
      shader.uniforms.maxTime = { value: state.crowdSpeed };
      shader.uniforms.fovScale = { value: 1 };

      shader.uniforms.waveEnabled = {
        value: this.contextRef.state.waveEnabled,
      };
      shader.uniforms.isSquare = { value: spriteName == "square" };
      shader.uniforms.rate = { value: 0.075 };
      shader.uniforms.wavelength = { value: 0.25 };
      shader.uniforms.amplitude = { value: 0.5 };
      shader.uniforms.spokes = { value: 2 };
      shader.uniforms.zOffset = { value: 60.0 };

      const declarations = `
          uniform float time;
          uniform float maxHeight;
          uniform float maxTime;
          uniform float fovScale;

          uniform bool waveEnabled;
          uniform bool isSquare;
          uniform float rate;
          uniform float wavelength;
          uniform float amplitude;
          uniform float spokes;
          uniform float zOffset;

          attribute float timer;
          attribute float state;
        `;

      shader.vertexShader =
        declarations +
        shader.vertexShader.replace(
          "#include <begin_vertex>",
          `
            vec3 transformed = vec3(position);

            float dt = time - timer;

            if ((timer == 0.0f) || (abs(dt) > maxTime))
            {
              // settle
              if (state > 0.5f)
              {
                transformed.y = transformed.y + maxHeight;
              }
            }
            else
            {
              // interpolate
              // float t_mod = (dt - (maxTime * floor(dt / maxTime))) / maxTime;
              float t_mod = dt / maxTime;
              float alpha = -(cos(t_mod * PI) - 1.0f) / 2.0f;

              if (state > 0.5f)
              {
                // stand up
                transformed.y = transformed.y + (maxHeight * alpha);
              }
              else
              {
                // sit down
                transformed.y = transformed.y + (maxHeight * (1.0f - alpha));
              }
            }

            //wave effect
            if (waveEnabled && isSquare) {
              vec2 sign = transformed.xz + vec2(0, zOffset);
              for (float t_offset = 0.0f; t_offset < PI; t_offset += PI/spokes) {
                vec2 spoke = normalize(vec2(cos(rate*time + t_offset), sin(rate*time + t_offset)));
                vec2 projection = dot(sign, spoke)*spoke;
                vec2 rejection = sign - projection;
                float v = wavelength * clamp(-PI_HALF/wavelength, PI_HALF/wavelength, length(rejection));
                float h = wavelength * clamp(-PI/wavelength, PI/wavelength, length(rejection));
                transformed.y += amplitude * cos(v);
                transformed.xz += 0.5f * amplitude * sin(h) * normalize(rejection);
              }
            }
          `
        );

      shader.vertexShader = shader.vertexShader.replace(
        "gl_PointSize = size;",
        "gl_PointSize = size * fovScale;"
      );

      this.shaderDetails[spriteName].shader = shader;
    };
    pointMat.customProgramCacheKey = () => {
      return spriteName == "square" ? "1" : "0";
    };

    return pointMat;
  }

  set tifoEnabled(on: boolean) {
    const color = this.tifoColors[on ? "circle" : "square"];
    if (this.points.square && this.shaderDetails.square.attributes && color) {
      this.shaderDetails.square.attributes.color.copy(color);
      this.shaderDetails.square.attributes.color.needsUpdate = true;
      this.points.square.material = this.materials[on ? "square" : "circle"];
    }
  }
}

function emod(n: number, m: number) {
  return ((n % m) + m) % m;
}

class TextureLookup {
  private imageData: ImageData;
  private constructor(imageData: ImageData) {
    this.imageData = imageData;
  }

  lookUp(u: number, v: number): { r: number; g: number; b: number; a: number } {
    var tx = Math.min(
      (emod(u, 1) * this.imageData.width) | 0,
      this.imageData.width - 1
    );
    var ty = Math.min(
      (emod(v, 1) * this.imageData.height) | 0,
      this.imageData.height - 1
    );
    var offset = (ty * this.imageData.width + tx) * 4;
    var r = this.imageData.data[offset + 0];
    var g = this.imageData.data[offset + 1];
    var b = this.imageData.data[offset + 2];
    var a = this.imageData.data[offset + 3];

    return { r, g, b, a };
  }

  static async make(src: string) {
    const img = new Image();
    img.src = src;
    img.crossOrigin = "Anonymous";
    await img.decode();

    const tempCanvas = document.createElement("canvas");
    tempCanvas.style.display = "none";

    const ctx = tempCanvas.getContext("2d");
    if (ctx) {
      ctx.canvas.width = img.width;
      ctx.canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, img.width, img.height);

      return new TextureLookup(imageData);
    } else {
      return undefined;
    }
  }
}

function getTopEdgeIndices(
  yCoords: number[],
  tolerance = 0.8
): [number, number] | undefined {
  const pairs = new Map([
    [0, [1, 2]],
    [1, [0, 2]],
  ]);

  for (const [check, against] of pairs) {
    if (
      almostEqual(yCoords[check], yCoords[against[0]], tolerance) &&
      yCoords[check] > yCoords[against[1]]
    ) {
      return [check, against[0]];
    } else if (
      almostEqual(yCoords[check], yCoords[against[1]], tolerance) &&
      yCoords[check] > yCoords[against[0]]
    ) {
      return [check, against[1]];
    }
  }

  return undefined;
}

interface ColorChance {
  colors: Color[];
  chance?: number;
}

class ColorGenerator {
  colors: {
    colors: Color[];
    test: number;
  }[];

  private logoLookup?: TextureLookup;

  private keyedColors: {
    home: ColorChance[];
    away: ColorChance[];
    other: ColorChance[];
  } = {
    home: [
      { colors: [], chance: 0.185 },
      { colors: [], chance: 0.155 },
      { colors: [], chance: 0.12 },
      { colors: [], chance: 0.08 },
      { colors: [], chance: 0.05 },
    ],
    away: [
      { colors: [], chance: 0.13 },
      { colors: [], chance: 0.095 },
      { colors: [], chance: 0.075 },
      { colors: [], chance: 0.045 },
      { colors: [], chance: 0.02 },
    ],
    other: [
      {
        colors: [0xb8b6b0, 0xaeb2a6, 0xffffff, 0xf5f5f5, 0xa4afae].map(
          (c) => new Color(c)
        ),
      },
    ],
  };

  private color: Color = new Color();

  constructor(
    fanColors: FanColors,
    logoLookup?: TextureLookup,
    home?: string,
    away?: string
  ) {
    if (home === undefined) home = "default";
    if (away === undefined) away = "default";

    this.logoLookup = logoLookup;

    const homeColors: string[] =
      (fanColors[home]?.length || 0) >= 3 ? fanColors[home] : fanColors.default;
    const awayColors: string[] =
      (fanColors[away]?.length || 0) >= 3 ? fanColors[away] : fanColors.default;

    // TODO: remove after mc sponsorship
    const mcColors = [0xea001b, 0xf79e1c, 0xff5f01];

    for (let i = 0; i < homeColors.length; i++) {
      this.keyedColors.home[i].colors.push(new Color(mcColors[i % 3]));
    }
    for (let i = 0; i < awayColors.length; i++) {
      this.keyedColors.away[i].colors.push(new Color(mcColors[i % 3]));
    }

    this.colors = [];

    this.resetColors();
  }

  private resetColors() {
    let accum = 0;
    const initColors = [
      ...this.keyedColors.home,
      ...this.keyedColors.away,
      ...this.keyedColors.other,
    ];

    for (const color of initColors) {
      accum = color.chance !== undefined ? accum + color.chance : 1;

      if (accum > 1) {
        console.error("color chance accumulator saturated");
      }

      this.colors.push({
        colors: color.colors.map((c) => {
          const col = new Color(c);
          return col;
        }),
        test: accum,
      });
    }
  }

  generate(): [number, number, number] {
    if (Math.random() > 0.4) {
      const test = Math.random();

      for (const color of this.colors) {
        if (test < color.test) {
          this.color = chooseRandom(color.colors);
          break;
        }
      }
    }

    if (Math.random() < 0.0005) {
      this.resetColors();
    }

    // 5% luminousity change - normally distributed
    const dL = (randomNormal() - 0.5) * 0.02;
    this.color = changeLS(this.color, 1 + dL);

    return [this.color.r, this.color.g, this.color.b];
  }

  genSpecial(pos: Vector3): [number, number, number] | undefined {
    const alpha = 4;
    const z = pos.z + 40;

    const angle = Math.atan2(z, pos.x);
    const dist = Math.sqrt(pos.x ** 2 + pos.y ** 2 + z ** 2) - 5;

    if (this.logoLookup) {
      const logoColor = this.logoLookup.lookUp(
        (alpha * angle) / 1,
        (alpha * dist) / 100
      );
      if (logoColor.a !== 0) {
        return [logoColor.r / 255, logoColor.g / 255, logoColor.b / 255];
      }
    }
    return undefined;
  }
}

function chooseRandom<T>(list: T[]): T {
  return list[Math.floor(Math.random() * list.length)];
}

function changeLS(color: Color, relL = 1, relS = 0.9999999): Color {
  const hsl = { h: 0, s: 0, l: 0 };
  color.getHSL(hsl);
  color.setHSL(hsl.h, hsl.s * relS, hsl.l * relL);

  return color;
}
